/**
 * Client-side port of the source project's `serverHelpers`.
 *
 * In Next these functions read `cookies()` on the server to pick the theme
 * before the first render. In a Vite SPA we read the same cookie straight from
 * the browser — key and format are unchanged, so the Customizer keeps working.
 */
import type { Settings } from '@/stores/settings'
import type { Mode, Skin, SystemMode } from '@core/types'

import theme from '@configs/theme'
import themeConfig from '@configs/theme-config'

const readCookie = (name: string): string | undefined => {
  if (typeof document === 'undefined') return undefined

  const match = document.cookie.match(
    new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)')
  )

  return match ? decodeURIComponent(match[1]) : undefined
}

/**
 * Preferences written by the Customizer. With `userCustomizable: false` in
 * `src/configs/theme.ts` the cookie is ignored: the theme is the file's, and
 * nobody who visited before stays stuck on an old choice.
 */
export const getSettingsFromCookie = (): Settings => {
  if (!theme.userCustomizable) return {}

  try {
    return JSON.parse(readCookie(themeConfig.settingsCookieName) || '{}')
  } catch {
    return {}
  }
}

export const getMode = (): Mode => getSettingsFromCookie().mode || themeConfig.mode

/** Effective mode when `mode === 'system'`: asks prefers-color-scheme. */
export const getSystemMode = (): SystemMode => {
  const mode = getMode()

  if (mode !== 'system') return mode

  if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark'

  return 'light'
}

export const getServerMode = (): SystemMode => {
  const mode = getMode()

  return mode === 'system' ? getSystemMode() : mode
}

export const getSkin = (): Skin => getSettingsFromCookie().skin || 'default'
